import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'

import { enableLoginWithUsername } from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   Config,
   enableBackendBaseUrlChange
} from '@flowwai-frontend/bps-officer-web/env-config'
import {
   LeftAlignedLoginLayoutConfig,
   useClientConfig
} from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   isLargeDesktop,
   isMediumDesktop
} from '@flowwai-frontend/shared/utils/web'

import {
   loginMachineTags,
   NO_OF_OTP_BOXES
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'
import {
   getIsSessionLogoutModalOpen,
   setIsSessionLogoutModalOpen
} from '../../utils/StorageUtils'

import EnvironmentSelector from '../EnvironmentSelector/EnvironmentSelector'
import { OtpVerificationStepV2 } from '../OtpVerificationStepV2/OtpVerificationStepV2'
import SessionLogoutModal from '../SessionLogoutModal/SessionLogoutModal'
import LoginWithEmailV2 from '../UserLoginV2/LoginWithEmailV2'
import LoginWithUsernameV2 from '../UserLoginV2/LoginWithUsernameV2'

import './index.css'
import * as Styles from './styles'

interface LoginProps {
   loginMachineState: LoginMachineState
   loginMachineSend: LoginMachineSend
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
}

const LoginV2 = (props: LoginProps): ReactElement => {
   const { loginMachineState, loginMachineSend, timerRef } = props

   const clientConfig = useClientConfig()
   const { login: clientLoginConfig, loginLayout } = clientConfig
   const { poster, titleConfig } = loginLayout as LeftAlignedLoginLayoutConfig

   //TODO: need to simplify the logic
   const isModalOpen = getIsSessionLogoutModalOpen() === 'true' ? true : false

   const [isOpenSessionModal, setIsOpenSessionModal] = useState(isModalOpen)

   const [showLoginTransition, setShowLoginTransition] = useState(false)

   const showEnvSelectors = enableBackendBaseUrlChange(Config)
   const isMediumDevice = isMediumDesktop()

   const { theme } = useAppTheme()
   const { common: loginTheme } = theme.auth.login

   const isLeftAlignedLoginLayout =
      clientConfig.loginLayout.type === 'LEFT_ALIGNED'

   const onCloseSessionModal = (): void => {
      setIsSessionLogoutModalOpen(false)
      setIsOpenSessionModal(false)
   }

   const renderAppLogo = (): ReactElement => {
      const loginImgUrl =
         clientLoginConfig.mediumLogoURL && !isLargeDesktop()
            ? clientLoginConfig.mediumLogoURL
            : clientLoginConfig.logoURL

      return (
         <div className={cn(Styles.appLogoStyles)}>
            <img
               style={{ width: `${clientLoginConfig.logoWidth}px` }}
               alt='App Logo'
               src={loginImgUrl}
            />
         </div>
      )
   }

   const renderLoginBasedOnMachineState = (): ReactElement | null => {
      const isLoginWithEmail = loginMachineState.hasTag(
         loginMachineTags.loginScreen
      )

      const isLoginWithOTP = loginMachineState.hasTag(
         loginMachineTags.otpScreen
      )
      if (isLoginWithEmail) {
         return (
            <>
               {isLeftAlignedLoginLayout ? renderAppLogo() : null}
               <div
                  className={cn(
                     { 'login-enter-transition': showLoginTransition },
                     'w-[437px] mt-[32px]'
                  )}
               >
                  {enableLoginWithUsername ? (
                     <LoginWithUsernameV2
                        loginMachineSend={loginMachineSend}
                        loginMachineState={loginMachineState}
                        clientConfig={clientConfig}
                     />
                  ) : (
                     <LoginWithEmailV2
                        loginMachineSend={loginMachineSend}
                        loginMachineState={loginMachineState}
                        clientConfig={clientConfig}
                     />
                  )}
                  {showEnvSelectors && (
                     <EnvironmentSelector containerClassName={`w-[437px]`} />
                  )}
               </div>
            </>
         )
      } else if (isLoginWithOTP) {
         return (
            <>
               {isLeftAlignedLoginLayout ? renderAppLogo() : null}
               <OtpVerificationStepV2
                  otpFieldsCount={NO_OF_OTP_BOXES}
                  loginMachineState={loginMachineState}
                  loginMachineSend={loginMachineSend}
                  timerRef={timerRef}
                  setShowLoginTransition={setShowLoginTransition}
               />
            </>
         )
      }
      return null
   }

   const renderDefaultLayout = (): ReactElement => (
      <div className={cn(Styles.containerStyles, loginTheme.loginBGColor)}>
         <div className={cn(Styles.headerContainerStyles)}>
            <img
               alt='App Logo'
               src={clientLoginConfig.logoURL}
               style={{ width: `${clientLoginConfig.logoWidth}px` }}
            />
         </div>
         <div
            className={cn(Styles.bodyContainerStyles, loginTheme.loginBGColor)}
         >
            {renderLoginBasedOnMachineState()}
         </div>
         <p
            className={cn(
               Styles.footerDetailStyles,
               loginTheme.footerDetailsTextColor
            )}
         >
            {clientConfig.rightRegistered}
         </p>
         <div
            className={cn(Styles.footerBackgroundImageContainerStyles)}
            style={{
               backgroundImage: `url(${loginLayout.footerImageUrl})`,
               backgroundSize: 'contain',
               height: `${loginLayout.footerHeight}px`
            }}
         />
      </div>
   )

   const renderLeftAlignedLayoutRightSection = (): React.ReactElement => (
      <div
         className={`${Styles.loginRightContainer(
            isMediumDevice
         )} login-right-section`}
         // style={{ backgroundImage: `url(${poster.patternUrl})` }}
      >
         <div className={Styles.loginRightSubContainer}>
            <div className='relative'>
               <img
                  src={poster.imageUrl}
                  alt='portal-map-logo'
                  className={'map-image'}
               />
               <div className={Styles.cmNameContainer}>
                  <p
                     className={Styles.chiefMinisterNameStyles}
                     style={{
                        color: titleConfig?.nameColor
                     }}
                  >
                     {titleConfig?.name}
                  </p>

                  <p
                     className={Styles.chiefMinisterInfoStyles}
                     style={{
                        color: titleConfig?.salutationColor
                     }}
                  >
                     {titleConfig?.salutation.map(salutation => (
                        <p>{salutation}</p>
                     ))}
                  </p>
               </div>
            </div>
         </div>
      </div>
   )

   const renderLeftAlignedLayout = (): ReactElement => {
      const layoutConfig = loginLayout as LeftAlignedLoginLayoutConfig

      return (
         <div
            className={cn(
               Styles.leftAlignedContainerStyles,
               loginTheme.loginBGColor,
               'left-aligned-login-container'
            )}
            style={{
               backgroundImage: `url(${layoutConfig.bgImgUrl})`
            }}
         >
            <div className='flex flex-1 h-full relative overflow-y-auto scrollbar'>
               <div className={Styles.leftAlignedLoginContainerStyles}>
                  <div className={cn(Styles.bodyContainerStyles)}>
                     {renderLoginBasedOnMachineState()}
                     <p
                        className={cn(
                           Styles.reservedNameV2Styles,
                           loginTheme.footerDetailsTextV2Color,
                           loginTheme.footerDetailsBgColor
                        )}
                     >
                        {clientConfig.rightRegistered}
                     </p>
                  </div>
               </div>
               <div
                  className={Styles.leftAlignedRightContainerStyles(
                     isMediumDevice
                  )}
               >
                  {renderLeftAlignedLayoutRightSection()}
               </div>
            </div>
            {/* <div
               className={cn(Styles.footerBackgroundImageContainerStyles)}
               style={{
                  backgroundImage: `url(${layoutConfig.footerImageUrl})`,
                  height: `${layoutConfig.footerHeight}px`
               }}
            >
               <div className={Styles.organizationNameContainer}>
                  <p
                     className={cn(
                        Styles.reservedNameStyles,
                        loginTheme.footerDetailsTextColor
                     )}
                  >
                     {clientConfig.rightRegistered}
                  </p>
               </div>
            </div> */}
         </div>
      )
   }

   const renderBasedOnLayout = (): ReactElement => {
      switch (loginLayout.type) {
         case 'LEFT_ALIGNED':
            return renderLeftAlignedLayout()
         case 'DEFAULT':
         default:
            return renderDefaultLayout()
      }
   }

   return (
      <>
         {renderBasedOnLayout()}
         <SessionLogoutModal
            isOpen={isOpenSessionModal}
            onCloseModal={onCloseSessionModal}
         />
      </>
   )
}

export default observer(LoginV2)
